import React, { ReactNode } from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'; // shadcn tooltip
import { useAuth } from '@/context/CoginitoAuthProvider';
import { UserRoleEnum } from '@/types/User';
import { cn } from '@/utils';

interface AdminActionWrapperProps {
  // role?: string;
  allowedRole?: string; // default "admin"
  children: ReactNode;
  disabledClassName?: string;
  tooltipMessage?: string;
}

/**
 * Wraps any clickable element and restricts action if not allowed role.
 */
export const AdminActionWrapper: React.FC<AdminActionWrapperProps> = ({
  // role,
  allowedRole = UserRoleEnum.READ_AND_WRITE_ADMIN,
  children,
  disabledClassName = 'pointer-events-auto opacity-50 cursor-not-allowed',
  tooltipMessage = "You've readonly access!",
}) => {
  const { dbUser } = useAuth();
  const isAllowed =
    (dbUser?.role ?? UserRoleEnum.READ_AND_WRITE_ADMIN) === allowedRole;

  const handleBlockedAction = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const wrappedChild: any = React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement, {
        className: cn(
          (children as React.ReactElement).props.className,
          !isAllowed && disabledClassName,
        ),
        onClick: isAllowed
          ? (children as React.ReactElement).props.onClick
          : handleBlockedAction,
        onKeyDown: isAllowed
          ? (children as React.ReactElement).props.onKeyDown
          : (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') handleBlockedAction(e);
            },
        tabIndex: isAllowed ? 0 : -1,
        // Disable real <button> elements, without discarding the child's own
        // disabled state (e.g. a button that is busy mid-request).
        disabled:
          (children as React.ReactElement).props.disabled !== undefined
            ? (children as React.ReactElement).props.disabled || !isAllowed
            : undefined,
        href: isAllowed
          ? (children as React.ReactElement).props.href
          : undefined, // Prevent navigation for links
      })
    : children;

  if (isAllowed) {
    return wrappedChild;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{wrappedChild}</TooltipTrigger>
        <TooltipContent className="text-red-500">
          {tooltipMessage}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
